<template>
  <div
    class="wt-prompt-templates -ai-settings"
    :class="{ '-hovered': textareaActive, '-active': isDropdownOpen }"
  >
    <wt-dropdown-view
      ref="dropdown"
      type="top left auto"
      :disabled="disabled"
      :transient-view="true"
      @opened="onDropdownOpened"
      @closed="onDropdownClosed"
      :disable-click-outside="modalOpened"
    >
      <template slot="label">
        <i
          class="wticon wticon-document -template-icon"
          title="Prompt Templates"
        />
      </template>
      <template slot="view">
        <div class="-templates-content">
          <!-- Template List -->
          <div class="-section">
            <div
              v-if="templates.length === 0 && !loading"
              class="-section-header"
              v-text="'No Templates Yet'"
            />
            <div v-if="templates.length > 0" style="position: relative">
              <input
                type="text"
                class="-template-search-input"
                ref="searchInput"
                v-model="searchQuery"
                placeholder=""
              />
              <div
                v-if="!searchQuery"
                class="-search-placeholder"
                @click="$refs.searchInput.focus()"
              >
                Search templates...
              </div>
            </div>
            <div v-if="loading" class="-section -loading">
              <wt-google-like-loader v-show="true" class="-default -spinner" />
            </div>
            <ul v-if="templates.length" class="-templates-list">
              <li
                v-for="template in filteredTemplates"
                :key="template.id"
                @click="selectTemplate(template)"
                class="-template-item"
              >
                <span
                  class="-template-name"
                  :title="template.name"
                  v-text="template.name"
                />
                <div class="-actions" v-if="template.scope !== 'PUBLIC'">
                  <i
                    class="wticon wticon-pencil3 -edit-icon"
                    @click.stop="showEditDialog(template)"
                    title="Edit Template"
                  />
                  <i
                    class="wticon wticon-trash2 -delete-icon"
                    @click.stop="confirmDeleteTemplate(template)"
                    title="Delete Template"
                  />
                </div>
              </li>
            </ul>
          </div>

          <!-- Save Template -->
          <div class="-section">
            <button
              class="-action-button"
              :disabled="!canSave"
              @click="showSaveDialog"
            >
              <i class="wticon wticon-download2" />
              <span>Save as template</span>
            </button>
          </div>
        </div>
      </template>
    </wt-dropdown-view>
  </div>
</template>

<script>
import app from 'app';
import wtDropdownView from '#/components/dropdowns/wtDropdownView.component.vue';

export default {
  name: 'wtPromptTemplates',
  components: { wtDropdownView },

  props: {
    prompt: { type: String, default: '' },
    textareaActive: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },

  data() {
    return {
      modalOpened: false,
      templates: [],
      loading: false,
      isDropdownOpen: false,
      maxTemplateNameLength: 128,
      searchQuery: '',
    };
  },

  computed: {
    canSave() {
      return this.prompt && !this.loading && this.prompt.trim().length > 0;
    },
    filteredTemplates() {
      if (!this.searchQuery?.trim()) return this.templates;
      const query = this.searchQuery.toLowerCase().trim();
      return this.templates.filter((t) => t.name.toLowerCase().includes(query));
    },
  },

  mounted() {
    this.loadTemplates();
  },

  watch: {
    loading(newVal) {
      this.recalculateDropdownHeight();
    },
    filteredTemplates() {
      this.recalculateDropdownHeight();
    },
  },

  methods: {
    setModalOpened(value) {
      this.modalOpened = value;
    },

    onDropdownOpened() {
      this.isDropdownOpen = true;
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },

    onDropdownClosed() {
      this.isDropdownOpen = false;
      this.searchQuery = '';
    },

    loadTemplates() {
      this.loading = true;
      app.api.promptTemplates
        .getTemplates()
        .then((templates) => {
          this.templates = templates;
          this.recalculateDropdownHeight();
        })
        .catch((error) => {
          console.error('Failed to load templates:', error);
          app.dialog.error({
            type: app.dialog.types.ERROR_1,
            title: 'Failed to load templates',
            description: 'Please try again later.',
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    selectTemplate(template) {
      this.$emit('template-selected', template.promptText);
      this.$refs.dropdown.close();
    },

    showSaveDialog() {
      this.setModalOpened(true);
      const defaultName = this.generateDefaultName();

      app
        .showModal({
          type: 'fluid',
          component: 'wt-prompt-template-name-input',
          componentPropsData: {
            defaultValue: defaultName,
            maxLength: this.maxTemplateNameLength,
            onConfirm: (templateName) => {
              this.handleSaveTemplate(templateName);
            },
          },
        })
        .$on('close', () => {
          this.setModalOpened(false);
        });
    },

    showEditDialog(template) {
      this.setModalOpened(true);

      app
        .showModal({
          type: 'fluid',
          component: 'wt-prompt-template-name-input',
          componentPropsData: {
            defaultValue: template.name,
            promptText: template.promptText,
            maxLength: this.maxTemplateNameLength,
            onConfirm: (templateName, promptText) => {
              this.handleEditTemplate(template, templateName, promptText);
            },
          },
        })
        .$on('close', () => {
          this.setModalOpened(false);
        });
    },

    async handleEditTemplate(template, newName, promptText) {
      if (!newName || !newName.trim()) {
        this.setModalOpened(false);
        return;
      }

      const currentTemplate = this.templates.find((t) => t.id === template.id);
      let oldName;
      let oldPromptText;
      if (currentTemplate) {
        oldName = currentTemplate.name;
        oldPromptText = currentTemplate.promptText;
        currentTemplate.name = newName;
        currentTemplate.promptText = promptText;
      }
      try {
        await this.editTemplate(template.id, newName, promptText);
      } catch (error) {
        if (currentTemplate) {
          currentTemplate.name = oldName;
          currentTemplate.promptText = oldPromptText;
        }
      }
    },

    editTemplate(id, newName, promptText) {
      return new Promise((resolve, reject) => {
        const template = {
          id: id,
          name: newName,
          promptText: promptText,
          description: '',
          scope: 'ORGANIZATION',
        };

        app.api.promptTemplates
          .saveTemplate(template)
          .then((templateResponse) => {
            const index = this.templates.findIndex((t) => t.id === id);
            if (index !== -1) {
              this.templates.splice(index, 1, templateResponse);
            }
            this.setModalOpened(false);
            resolve();
          })
          .catch((error) => {
            this.setModalOpened(false);
            console.error('Failed to edit template:', error);
            app.dialog.error({
              type: app.dialog.types.ERROR_1,
              title: 'Failed to rename template',
              description: error.error || 'Please try again.',
            });
            reject();
          });
      });
    },

    handleSaveTemplate(templateName) {
      if (templateName && templateName.trim()) {
        // Check if name already exists
        app.api.promptTemplates
          .findTemplateByName(templateName)
          .then((existing) => {
            if (existing) {
              // Confirm override
              app.dialog
                .confirm({
                  type: app.dialog.types.CONFIRM_3,
                  title: 'Template name exists',
                  description: `A template named "${_.escape(
                    existing.name
                  )}" already exists. Override it?`,
                  buttonYesText: 'Override',
                  buttonNoText: 'Cancel',
                })
                .then((confirmResult) => {
                  this.setModalOpened(false);
                  if (confirmResult.action) {
                    this.saveTemplate(existing.id, templateName);
                  }
                });
            } else {
              // Save new template
              this.saveTemplate(null, templateName);
            }
          });
      }
    },

    saveTemplate(id, name) {
      const template = {
        id: id,
        name: name,
        promptText: this.prompt,
        description: '',
        scope: 'ORGANIZATION',
      };

      app.api.promptTemplates
        .saveTemplate(template)
        .then((templateResponse) => {
          // Remove existing template if updating
          if (id) {
            this.templates = this.templates.filter((t) => t.id !== id);
          }
          this.templates.unshift(templateResponse);
          this.searchQuery = '';
          this.setModalOpened(false);
          this.recalculateDropdownHeight();
        })
        .catch((error) => {
          this.setModalOpened(false);
          console.error('Failed to save template:', error);
          app.dialog.error({
            type: app.dialog.types.ERROR_1,
            title: 'Failed to save template',
            description: error.error || 'Please try again.',
          });
        });
    },

    trimTemplateName(name) {
      return name.length > 50 ? name.substring(0, 50) + '...' : name;
    },

    confirmDeleteTemplate(template) {
      this.setModalOpened(true);
      app.dialog
        .confirm({
          type: app.dialog.types.CONFIRM_3,
          title: `Delete template`,
          description: `Are you sure you want to delete the template "<span style="font-weight: 500;">${_.escape(
            this.trimTemplateName(template.name)
          )}</span>"?<br/><br/>Once deleted, it will no longer be available to anyone on your team.`,
          buttonYesText: 'Yes, Delete',
          buttonNoText: 'Cancel',
        })
        .then((confirmResult) => {
          this.setModalOpened(false);
          if (confirmResult.action) {
            this.deleteTemplate(template.id, template.name);
          }
        })
        .catch((error) => {
          this.setModalOpened(false);
        });
    },

    recalculateDropdownHeight() {
      this.$nextTick(() => {
        if (
          this.$refs.dropdown &&
          this.$refs.dropdown.$_setBoxSlidingNextTick
        ) {
          this.$refs.dropdown.$_setBoxSlidingNextTick();
        }
      });
    },

    deleteTemplate(id, name) {
      app.api.promptTemplates
        .deleteTemplate(id)
        .then(() => {
          this.templates = this.templates.filter(
            (template) => template.id !== id
          );
          this.setModalOpened(false);
          this.recalculateDropdownHeight();
        })
        .catch((error) => {
          this.setModalOpened(false);
          console.error('Failed to delete template:', error);
          app.dialog.error({
            type: app.dialog.types.ERROR_1,
            title: 'Failed to delete template',
            description: error.error || 'Please try again.',
          });
        });
    },

    generateDefaultName() {
      // Generate a default name from the first line of the prompt
      const firstLine = this.prompt
        .split('\n')[0]
        .substring(0, this.maxTemplateNameLength);
      return firstLine || 'Untitled Template';
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'style_guide/_sg_variables.scss';

.wt-prompt-templates {
  display: inline-block;

  .-template-icon {
    margin-right: 10px;
    font-size: 20px;
    color: rgba(0, 0, 0, 0.5);
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s ease, color 0.2s ease;

    &:hover {
      opacity: 1;
      color: $color-primary;
    }
  }

  &.-hovered {
    .-template-icon {
      opacity: 0.75;
    }
  }
  &.-active {
    .-template-icon {
      opacity: 1;
      color: $color-primary;
    }
  }
}

.-templates-content {
  min-width: 250px;
  max-width: 250px;

  .-section {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    &:last-child {
      border-bottom: none;
    }
  }

  .-section.-loading {
    padding: 7px 15px 15px;
  }

  .-section-header {
    color: rgba(0, 0, 0, 0.4);
    font-weight: 400;
    font-size: 11px;
    padding: 12px 15px 10px 15px;
    text-transform: uppercase;
    // letter-spacing: 0.5px;
  }

  .-template-search-input {
    width: calc(100% - 20px);
    margin: 10px 10px;
    padding: 5px 10px;
    font-size: 13px;
    border: 1px solid #d9d9d9;
    border-radius: 3px;
    outline: none;
    &:focus {
      border-color: $color-primary;
    }
  }

  .-search-placeholder {
    position: absolute;
    top: 10px;
    left: 10px;
    padding: 6px 10px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.4);
    pointer-events: none;
    cursor: text;
  }

  .-templates-list {
    padding: 0;
    margin: 0;
    list-style: none;
    max-height: 151px;
    overflow-y: auto;

    .-template-item {
      padding: 8px 5px 8px 15px;
      cursor: pointer;
      transition: background-color 0.15s ease;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      width: 100%;
      min-width: 0;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: rgba(6, 169, 244, 0.1);

        .-edit-icon,
        .-delete-icon {
          opacity: 1;
          &.-disabled {
            opacity: 0.25;
            cursor: default;
            &:hover {
              color: inherit;
            }
          }
        }
      }

      .-template-name {
        display: block;
        font-size: 13px;
        color: rgba(0, 0, 0, 0.85);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
        min-width: 0;
      }

      .-actions {
        display: flex;
        align-items: center;
        gap: 0;
        flex-shrink: 0;
      }

      .-edit-icon,
      .-delete-icon {
        font-size: 13px;
        opacity: 0;
        transition: opacity 0.15s ease, color 0.15s ease;
        cursor: pointer;
        padding: 4px;
      }

      .-edit-icon {
        &:hover {
          color: #3498db;
        }
      }

      .-delete-icon {
        &:hover {
          color: #c9302c;
        }
      }
    }
  }

  .-action-button {
    width: 100%;
    padding: 10px 15px;
    border: none;
    height: auto;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
    transition: background-color 0.15s ease;
    text-align: left;

    &:hover:not(:disabled) {
      background-color: rgba(6, 169, 244, 0.1);
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .wticon {
      font-size: 14px;
      margin: 0;
    }
  }
}
</style>
